// src/components/modals/EditEventModal.js
import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useAuth } from '../../context/AuthContext';
import * as bootstrap from 'bootstrap';
import axios from 'axios';
import API from '../../config/config'; // Updated to use config.js
import LoadingModal from './LoadingModal';

const EditEventModal = ({ event, show, handleClose }) => {
  const { currentUser } = useAuth();
  const [formData, setFormData] = useState({
    event_title: '',
    event_date: '',
    event_time: '',
    event_end_time: '',
    location: '',
    event_summary: '',
    event_description: '',
    status: 'active'
  });
  const [eventGraphic, setEventGraphic] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  const modalRef = useRef(null);
  const modalInstance = useRef(null);
  const fileInputRef = useRef(null);

  // Initialize the modal when the component mounts
  useEffect(() => {
    if (modalRef.current) {
      modalInstance.current = new bootstrap.Modal(modalRef.current, {
        backdrop: 'static',
        keyboard: false
      });
    }
  }, []);

  // Show/hide modal based on the show prop
  useEffect(() => {
    if (show && modalInstance.current) {
      modalInstance.current.show();
    } else if (!show && modalInstance.current) {
      modalInstance.current.hide();
    }
  }, [show]);

  // Listen for modal hidden event to call handleClose
  useEffect(() => {
    const modalEl = modalRef.current;
    if (modalEl) {
      modalEl.addEventListener('hidden.bs.modal', handleClose);
      return () => {
        modalEl.removeEventListener('hidden.bs.modal', handleClose);
      };
    }
  }, [handleClose]);

  // Initialize form data when event props change
  useEffect(() => {
    if (event) {
      
      // Format the date to YYYY-MM-DD for the date input
      let formattedDate = '';
      if (event.event_date) {
        try {
          const date = new Date(event.event_date);
          formattedDate = date.toISOString().split('T')[0];
        } catch (e) {
          console.error("Error formatting date:", e);
          formattedDate = event.event_date;
        }
      }
      
      // Explicitly set all fields, being careful with the event_description
      setFormData({
        event_title: event.event_title || '',
        event_date: formattedDate,
        event_time: event.event_time || '',
        event_end_time: event.event_end_time || '',
        location: event.location || '',
        event_summary: event.event_summary || '',
        event_description: event.event_description || '',  // Make sure this is set correctly
        status: event.status || 'active'
      });

      // Set preview URL if event has a graphic - Fix URL construction
      if (event.event_graphic) {
        // Fix the duplicate path issue
        const imageUrl = event.event_graphic.startsWith('http') 
          ? event.event_graphic 
          : `${API.BASE_URL}/uploads/${event.event_graphic}`; // Remove duplicate "events" folder
        setPreviewUrl(imageUrl);
      } else {
        setPreviewUrl(null);
      }
    }
  }, [event]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setEventGraphic(file);
      
      // Create a preview URL
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewUrl(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const triggerFileInput = () => {
    fileInputRef.current.click();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    const updateData = new FormData();
    // Add all form fields to FormData
    Object.keys(formData).forEach(key => {
      updateData.append(key, formData[key]);
    });

    // Add event graphic if selected
    if (eventGraphic) {
      updateData.append('event_graphic', eventGraphic);
    }

    try {
      // Use the correct API endpoint for updating events
      const response = await axios.put(
        `${API.BASE_URL}/api/admin/events/${event.id}`, // Updated to use admin endpoint
        updateData,
        {
          headers: {
            'Authorization': `Bearer ${currentUser.token}`,
            // Don't set Content-Type for FormData
          }
        }
      );

      if (response.data && response.data.success) {
        setMessage({ type: 'success', text: 'Event updated successfully!' });
        
        // Update preview URL if new image was uploaded - Fix URL construction
        if (response.data.event && response.data.event.event_graphic) {
          const newImageUrl = `${API.BASE_URL}/uploads/${response.data.event.event_graphic}`;
          setPreviewUrl(newImageUrl);
        }

        setTimeout(() => {
          // Close the modal after success
          if (modalInstance.current) {
            modalInstance.current.hide();
          }
        }, 1500);
      } else {
        setMessage({ type: 'danger', text: response.data?.message || 'Failed to update event' });
      }
    } catch (error) {
      console.error('Error updating event:', error);
      const errorMessage = error.response?.data?.message || error.message || 'An error occurred while updating the event.';
      setMessage({ type: 'danger', text: errorMessage });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {loading && <LoadingModal message="Updating event..." />}
      
      <div 
        className="modal fade"
        id="editEventModal"
        tabIndex="-1"
        ref={modalRef}
        aria-labelledby="editEventModalLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-lg">
          <div className="modal-content bg_grey">
            <div className="modal-header">
              <h5 className="modal-title text-white" id="editEventModalLabel">Edit Event</h5>
              <button
                type="button"
                className="btn-close btn-close-white"
                data-bs-dismiss="modal"
                aria-label="Close"
              ></button>
            </div>
            <div className="modal-body">
              <form onSubmit={handleSubmit}>
                {/* Event Image */}
                <div className="mb-3">
                  <label className="form-label text-white text-start d-block">Event Graphic</label>
                  <div 
                    onClick={triggerFileInput} 
                    style={{ 
                      cursor: 'pointer', 
                      border: '2px dashed #ccc', 
                      borderRadius: '5px',
                      padding: '20px',
                      textAlign: 'center'
                    }}
                  >
                    {previewUrl ? (
                      <img 
                        src={previewUrl} 
                        alt="Event preview" 
                        style={{ maxHeight: '200px', maxWidth: '100%' }} 
                      />
                    ) : (
                      <div>
                        <i className="fa fa-cloud-upload fa-3x text-white-50"></i>
                        <p className="text-white-50 mt-2">Click to upload image</p>
                      </div>
                    )}
                  </div>
                  <input 
                    type="file"
                    ref={fileInputRef}
                    style={{ display: 'none' }}
                    accept="image/*"
                    onChange={handleFileChange}
                  />
                </div>

                {/* Event Title */}
                <div className="mb-3">
                  <label htmlFor="edit-event-title" className="form-label text-white text-start d-block">Event Title</label>
                  <input 
                    type="text"
                    className="form-control bg-dark text-white"
                    id="edit-event-title"
                    name="event_title"
                    value={formData.event_title}
                    onChange={handleChange}
                    required
                  />
                </div>

                {/* Event Summary */}
                <div className="mb-3">
                  <label htmlFor="edit-event-summary" className="form-label text-white text-start d-block">Event Summary</label>
                  <textarea
                    className="form-control bg-dark text-white"
                    id="edit-event-summary"
                    name="event_summary"
                    rows="2"
                    value={formData.event_summary}
                    onChange={handleChange}
                    required
                  ></textarea>
                </div>

                {/* Event Description */}
                <div className="mb-3">
                  <label htmlFor="edit-event-description" className="form-label text-white text-start d-block">Event Description</label>
                  <textarea
                    className="form-control bg-dark text-white"
                    id="edit-event-description"
                    name="event_description"
                    rows="4"
                    value={formData.event_description}
                    onChange={handleChange}
                  ></textarea>
                </div>

                {/* Date and Time */}
                <div className="row mb-3">
                  <div className="col-md-4">
                    <label htmlFor="edit-event-date" className="form-label text-white text-start d-block">Date</label>
                    <input
                      type="date"
                      className="form-control bg-dark text-white"
                      id="edit-event-date"
                      name="event_date"
                      value={formData.event_date}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div className="col-md-4">
                    <label htmlFor="edit-event-time" className="form-label text-white text-start d-block">Start Time</label>
                    <input
                      type="time"
                      className="form-control bg-dark text-white"
                      id="edit-event-time"
                      name="event_time"
                      value={formData.event_time}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div className="col-md-4">
                    <label htmlFor="edit-event-end-time" className="form-label text-white text-start d-block">End Time</label>
                    <input
                      type="time"
                      className="form-control bg-dark text-white"
                      id="edit-event-end-time"
                      name="event_end_time"
                      value={formData.event_end_time}
                      onChange={handleChange}
                    />
                  </div>
                </div>

                {/* Location */}
                <div className="mb-3">
                  <label htmlFor="edit-event-location" className="form-label text-white text-start d-block">Location</label>
                  <input
                    type="text"
                    className="form-control bg-dark text-white"
                    id="edit-event-location"
                    name="location"
                    value={formData.location}
                    onChange={handleChange}
                    required
                  />
                </div>

                {/* Status */}
                <div className="mb-3">
                  <label htmlFor="edit-event-status" className="form-label text-white text-start d-block">Status</label>
                  <select
                    className="form-select bg-dark text-white"
                    id="edit-event-status"
                    name="status"
                    value={formData.status}
                    onChange={handleChange}
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>

                {/* Messages */}
                {message.text && (
                  <div 
                    className={`alert alert-${message.type === 'error' ? 'danger' : message.type}`} 
                    style={{
                      backgroundColor: message.type === 'success' ? '#198754' : 
                                     message.type === 'error' || message.type === 'danger' ? '#dc3545' : 
                                     message.type === 'warning' ? '#ffc107' : '#0dcaf0',
                      borderColor: message.type === 'success' ? '#198754' : 
                                  message.type === 'error' || message.type === 'danger' ? '#dc3545' : 
                                  message.type === 'warning' ? '#ffc107' : '#0dcaf0',
                      color: message.type === 'success' || message.type === 'error' || message.type === 'danger' ? '#ffffff' : '#000000'
                    }}
                  >
                    {message.text}
                  </div>
                )}

                {/* Submit Button */}
                <div className="text-center">
                  <button type="submit" className="btn btn-danger" disabled={loading}>
                    {loading ? 'Saving...' : 'Update Event'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

EditEventModal.propTypes = {
  event: PropTypes.object.isRequired,
  show: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
};

export default EditEventModal;
